import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../utils/api';

const PlatformLogsPage = () => {
  const { currentUser } = useOutletContext();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total: 0,
    total_pages: 0
  });

  // Filter states
  const [filters, setFilters] = useState({
    event_type: '',
    severity: '',
    search: '',
    start_date: '',
    end_date: ''
  });

  // Expanded row for details
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchLogs = useCallback(async (page = 1) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pagination.page_size);
      
      if (filters.event_type) params.append('event_type', filters.event_type);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.search) params.append('search', filters.search);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axios.get(`${API}/super-admin/audit-logs?${params.toString()}`);
      setLogs(response.data.logs);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters, pagination.page_size]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/audit-logs/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch audit log stats:', error);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/audit-logs/event-types`);
      setEventTypes(response.data.event_types);
    } catch (error) {
      console.error('Failed to fetch event types:', error);
    }
  };

  useEffect(() => {
    fetchLogs(1);
    fetchStats();
    fetchEventTypes();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchLogs(1);
    }, 300);
    return () => clearTimeout(debounce);
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      event_type: '',
      severity: '',
      search: '',
      start_date: '',
      end_date: ''
    });
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      INFO: 'bg-blue-100 text-blue-800',
      WARNING: 'bg-yellow-100 text-yellow-800',
      CRITICAL: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[severity] || 'bg-gray-100 text-gray-800'}`}>
        {severity}
      </span>
    );
  };

  const getEventTypeLabel = (eventType) => {
    const labels = {
      AUTH_LOGIN_SUCCESS: 'Login Success',
      AUTH_LOGIN_FAILED: 'Login Failed',
      AUTH_LOGOUT: 'Logout',
      AUTH_PASSWORD_RESET_REQUEST: 'Password Reset Request',
      AUTH_PASSWORD_RESET_SUCCESS: 'Password Reset Success',
      AUTH_PASSWORD_CHANGED: 'Password Changed',
      ACCESS_DENIED: 'Access Denied',
      USER_CREATED: 'User Created',
      USER_UPDATED: 'User Updated',
      USER_DELETED: 'User Deleted',
      ORG_CREATED: 'Organisation Created',
      ORG_UPDATED: 'Organisation Updated',
      ORG_DELETED: 'Organisation Deleted',
      SUBSCRIPTION_CHANGED: 'Subscription Changed',
      SUBSCRIPTION_UPGRADED: 'Subscription Upgraded',
      SUBSCRIPTION_DOWNGRADED: 'Subscription Downgraded',
      SECURITY_RATE_LIMIT_EXCEEDED: 'Rate Limit Exceeded',
      SUPER_ADMIN_ACTION: 'Super Admin Action',
      DATA_EXPORTED: 'Data Exported',
      DATA_BACKUP_CREATED: 'Backup Created'
    };
    return labels[eventType] || eventType?.replace(/_/g, ' ');
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Check if user has access
  if (currentUser?.access_level !== 1 && currentUser?.access_level !== undefined) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900">Access Restricted</h3>
          <p className="text-gray-500 mt-1">Only Level 1 users can view platform logs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h1 className="text-3xl font-bold">Platform Logs</h1>
            <p className="text-purple-200 text-sm">Security and activity audit logs for compliance and monitoring</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="text-sm font-medium text-gray-500">Total Logs</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total_logs?.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="text-sm font-medium text-gray-500">Last 24h</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.last_24h?.total?.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-4 bg-blue-50">
            <div className="text-sm font-medium text-blue-600">Info Events (24h)</div>
            <div className="text-2xl font-bold text-blue-700 mt-1">{stats.last_24h?.info?.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-yellow-200 p-4 bg-yellow-50">
            <div className="text-sm font-medium text-yellow-600">Warnings (24h)</div>
            <div className="text-2xl font-bold text-yellow-700 mt-1">{stats.last_24h?.warning?.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-red-200 p-4 bg-red-50">
            <div className="text-sm font-medium text-red-600">Critical (24h)</div>
            <div className="text-2xl font-bold text-red-700 mt-1">{stats.last_24h?.critical?.toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* Security Alerts */}
      {stats && (stats.failed_logins_24h > 0 || stats.rate_limits_24h > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h3 className="font-medium text-amber-800">Security Alerts (Last 24 Hours)</h3>
              <div className="mt-1 text-sm text-amber-700">
                {stats.failed_logins_24h > 0 && (
                  <span className="mr-4">{stats.failed_logins_24h} failed login attempt{stats.failed_logins_24h > 1 ? 's' : ''}</span>
                )}
                {stats.rate_limits_24h > 0 && (
                  <span>{stats.rate_limits_24h} rate limit exceeded event{stats.rate_limits_24h > 1 ? 's' : ''}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="lg:col-span-2">
            <input
              type="text"
              placeholder="Search logs..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div>
            <select
              value={filters.event_type}
              onChange={(e) => handleFilterChange('event_type', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Events</option>
              {eventTypes.map(type => (
                <option key={type} value={type}>{getEventTypeLabel(type)}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">All Severities</option>
              <option value="INFO">Info</option>
              <option value="WARNING">Warning</option>
              <option value="CRITICAL">Critical</option>
            </select>
          </div>
          <div>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Start Date"
            />
          </div>
          <div className="flex gap-2">
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="End Date"
            />
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Clear filters"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-gray-600">Loading logs...</span>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-medium">No logs found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {getEventTypeLabel(log.event_type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getSeverityBadge(log.severity)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{log.actor?.email || log.actor?.user_id || '-'}</div>
                        <div className="text-xs text-gray-500">{log.actor?.user_type || ''}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {log.actor?.ip_address || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {log.target?.type ? (
                          <div>
                            <div className="text-sm text-gray-900">{log.target.name || log.target.id}</div>
                            <div className="text-xs text-gray-500">{log.target.type}</div>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {log.details && Object.keys(log.details).length > 0 && (
                          <button
                            onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                            className="text-purple-600 hover:text-purple-900 p-1 hover:bg-purple-50 rounded transition-colors"
                          >
                            <svg className={`w-5 h-5 transform transition-transform ${expandedRow === log.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedRow === log.id && log.details && (
                      <tr className="bg-gray-50">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="text-sm">
                            <div className="font-medium text-gray-700 mb-2">Event Details</div>
                            <div className="bg-white rounded-lg p-3 border border-gray-200">
                              <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                                {JSON.stringify(log.details, null, 2)}
                              </pre>
                            </div>
                            {log.actor?.user_agent && (
                              <div className="mt-2">
                                <span className="text-xs text-gray-500">User Agent: </span>
                                <span className="text-xs text-gray-700">{log.actor.user_agent}</span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!isLoading && logs.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{((pagination.page - 1) * pagination.page_size) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(pagination.page * pagination.page_size, pagination.total)}</span> of{' '}
              <span className="font-medium">{pagination.total}</span> logs
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchLogs(pagination.page - 1)}
                disabled={!pagination.has_prev}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  pagination.has_prev
                    ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <button
                onClick={() => fetchLogs(pagination.page + 1)}
                disabled={!pagination.has_next}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  pagination.has_next
                    ? 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Top Events (Last 7 Days) */}
      {stats?.top_events_7d?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Events (Last 7 Days)</h3>
          <div className="space-y-3">
            {stats.top_events_7d.map((event, index) => (
              <div key={event.event_type} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-500 w-6">{index + 1}.</span>
                  <span className="text-sm text-gray-900">{getEventTypeLabel(event.event_type)}</span>
                </div>
                <span className="text-sm font-medium text-gray-700">{event.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformLogsPage;
